import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ChevronRight,
  Menu,
  X,
  Moon,
  Sun,
  ArrowRight,
  BarChart,
  Brain,
  Target,
  TrendingUp,
  MessageCircle,
  Calendar,
  Award,
} from "lucide-react";
import { useTheme } from "./ThemeProvider";
import Button from "./ui/button";

export default function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const features = [
    {
      title: "AI-Powered Check-ins",
      description: "Get personalized insights and guidance through intelligent conversational check-ins.",
      icon: <Brain className="size-5" />,
    },
    {
      title: "Momentum Tracking",
      description: "Track your progress and maintain momentum with visual analytics and insights.",
      icon: <TrendingUp className="size-5" />,
    },
    {
      title: "Goal Setting",
      description: "Set clear, achievable goals and track your progress towards them.",
      icon: <Target className="size-5" />,
    },
    {
      title: "Conversational Support",
      description: "Get real-time support and guidance through natural conversations.",
      icon: <MessageCircle className="size-5" />,
    },
    {
      title: "Progress Analytics",
      description: "Visualize your journey with detailed analytics and progress reports.",
      icon: <BarChart className="size-5" />,
    },
    {
      title: "Daily Accountability",
      description: "Stay accountable with daily check-ins and progress tracking.",
      icon: <Calendar className="size-5" />,
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Product Manager",
      content: "ThinkMate has completely transformed how I approach my daily goals. The AI insights are incredibly helpful.",
      avatar: "SJ",
    },
    {
      name: "Michael Chen",
      role: "Software Engineer",
      content: "The momentum tracking feature keeps me motivated and focused on my long-term objectives.",
      avatar: "MC",
    },
    {
      name: "Emily Rodriguez",
      role: "Entrepreneur",
      content: "Finally, a tool that actually helps me stay accountable and make real progress.",
      avatar: "ER",
    },
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      description: "Perfect for getting started",
      features: [
        "5 daily check-ins",
        "Basic momentum tracking",
        "AI insights",
        "Mobile app access",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      price: "$12.99",
      period: "/month",
      description: "For serious goal achievers",
      features: [
        "Unlimited check-ins",
        "Advanced analytics",
        "Priority support",
        "Custom goals",
        "Export data",
        "Team features",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "For teams and organizations",
      features: [
        "Everything in Pro",
        "Team management",
        "Advanced reporting",
        "API access",
        "Dedicated support",
        "Custom integrations",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  const faqs = [
    {
      question: "How does ThinkMate help me achieve my goals?",
      answer: "ThinkMate uses AI-powered conversations to help you reflect on your progress, identify obstacles, and maintain momentum towards your goals. Our system learns from your patterns and provides personalized insights.",
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use industry-standard encryption and security practices to protect your personal data. Your conversations and progress data are private and secure.",
    },
    {
      question: "Can I use ThinkMate on mobile?",
      answer: "Yes! ThinkMate is fully responsive and works great on mobile devices. You can check in and track your progress anywhere, anytime.",
    },
    {
      question: "How often should I check in?",
      answer: "We recommend daily check-ins for best results, but you can adjust the frequency based on your preferences and goals. Consistency is key to building momentum.",
    },
  ];

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background-base dark:bg-gray-950">
      <header
        className={`sticky top-0 z-50 w-full backdrop-blur-lg transition-all duration-300 ${
          isScrolled ? "bg-white/80 dark:bg-gray-950/80 shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 flex h-16 items-center justify-between">
          <button 
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 font-bold text-xl hover:opacity-80 transition-opacity cursor-pointer focus:outline-none"
            aria-label="Refresh page"
          >
            <div className="size-8 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center text-white">
              T
            </div>
            <span className="text-text-primary dark:text-white">ThinkMate</span>
          </button>
          <nav className="hidden md:flex gap-8">
            <a
              href="#features"
              className="text-sm font-medium text-text-secondary dark:text-gray-300 transition-colors hover:text-text-primary dark:hover:text-white"
            >
              Features
            </a>
            <a
              href="#testimonials"
              className="text-sm font-medium text-text-secondary dark:text-gray-300 transition-colors hover:text-text-primary dark:hover:text-white"
            >
              Testimonials
            </a>
            <a
              href="#pricing"
              className="text-sm font-medium text-text-secondary dark:text-gray-300 transition-colors hover:text-text-primary dark:hover:text-white"
            >
              Pricing
            </a>
            <a
              href="#faq"
              className="text-sm font-medium text-text-secondary dark:text-gray-300 transition-colors hover:text-text-primary dark:hover:text-white"
            >
              FAQ
            </a>
          </nav>
          <div className="hidden md:flex gap-4 items-center">
            <button
              onClick={toggleTheme}
              className="inline-flex items-center justify-center rounded-full p-2 text-text-secondary dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {mounted && theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
              <span className="sr-only">Toggle theme</span>
            </button>
            <Link
              to="/login"
              className="text-sm font-medium text-text-secondary dark:text-gray-300 transition-colors hover:text-text-primary dark:hover:text-white"
            >
              Log in
            </Link>
            <Button
              onClick={() => navigate("/signup")}
              ambient={true}
              size="sm"
              className="px-4 py-2"
            >
              Get Started
            </Button>
          </div>
          <div className="flex items-center gap-4 md:hidden">
            <button
              className="inline-flex items-center justify-center rounded-full p-2 text-text-secondary dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
              <span className="sr-only">Toggle menu</span>
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-16 inset-x-0 bg-white/95 dark:bg-gray-950/95 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <a href="#features" className="py-2 text-sm font-medium text-text-primary dark:text-white" onClick={() => setMobileMenuOpen(false)}>
                Features
              </a>
              <a href="#testimonials" className="py-2 text-sm font-medium text-text-primary dark:text-white" onClick={() => setMobileMenuOpen(false)}>
                Testimonials
              </a>
              <a href="#pricing" className="py-2 text-sm font-medium text-text-primary dark:text-white" onClick={() => setMobileMenuOpen(false)}>
                Pricing
              </a>
              <a href="#faq" className="py-2 text-sm font-medium text-text-primary dark:text-white" onClick={() => setMobileMenuOpen(false)}>
                FAQ
              </a>
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
                <Link to="/login" className="py-2 text-sm font-medium text-text-primary dark:text-white" onClick={() => setMobileMenuOpen(false)}>
                  Log in
                </Link>
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate("/signup");
                  }}
                  ambient={true}
                  size="sm"
                  className="px-4 py-2"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-background-base to-blue-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 py-20 sm:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="mx-auto max-w-4xl text-center"
            >
              <motion.div variants={item} className="mb-8">
              </motion.div>
              <motion.h1
                variants={item}
                className="mb-6 text-4xl font-bold tracking-tight text-text-primary dark:text-white sm:text-6xl"
              >
                <span className="bg-gradient-to-r from-rose-600 to-blue-600 bg-clip-text text-transparent">
                  Achieve
                </span>{" "}
                Your{" "}
                <span className="bg-gradient-to-r from-rose-600 to-blue-600 bg-clip-text text-transparent">
                  Goals
                </span>{" "}
                with AI-Powered{" "}
                <span className="bg-gradient-to-r from-rose-600 to-blue-600 bg-clip-text text-transparent">
                  Momentum
                </span>
              </motion.h1>
              <motion.p
                variants={item}
                className="mb-8 text-lg text-text-secondary dark:text-gray-300 sm:text-xl"
              >
                Transform your daily check-ins into powerful insights. ThinkMate uses AI to help you stay accountable, 
                track progress, and maintain momentum towards your most important goals.
              </motion.p>
              <motion.div variants={item} className="flex justify-center">
                <Button
                  onClick={() => navigate("/signup")}
                  ambient={true}
                  size="lg"
                  className="px-8 py-3 text-base"
                >
                  Start Your Journey
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 sm:py-32 bg-background-base dark:bg-gray-950">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl font-bold tracking-tight text-text-primary dark:text-white sm:text-4xl mb-4">
                Everything you need to succeed
              </motion.h2>
              <motion.p variants={item} className="text-lg text-text-secondary dark:text-gray-300">
                Powerful features designed to help you build momentum and achieve your goals faster.
              </motion.p>
            </motion.div>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-7xl grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="group relative rounded-2xl border border-gray-200 dark:border-gray-800 p-8 hover:border-rose-200 dark:hover:border-rose-800 transition-colors"
                >
                  <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-text-primary dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary dark:text-gray-300">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 sm:py-32 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl font-bold tracking-tight text-text-primary dark:text-white sm:text-4xl mb-4">
                Loved by goal achievers worldwide
              </motion.h2>
              <motion.p variants={item} className="text-lg text-text-secondary dark:text-gray-300">
                See what our users have to say about their ThinkMate experience.
              </motion.p>
            </motion.div>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-4xl grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="rounded-2xl bg-white dark:bg-gray-800 p-8 shadow-sm"
                >
                  <div className="mb-4 flex items-center gap-4">
                    <div className="size-12 rounded-full bg-rose-100 dark:bg-rose-900/20 flex items-center justify-center text-rose-600 dark:text-rose-400 font-semibold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary dark:text-white">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-text-secondary dark:text-gray-300">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-secondary dark:text-gray-300">
                    "{testimonial.content}"
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 sm:py-32 bg-background-base dark:bg-gray-950">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl font-bold tracking-tight text-text-primary dark:text-white sm:text-4xl mb-4">
                Simple, transparent pricing
              </motion.h2>
              <motion.p variants={item} className="text-lg text-text-secondary dark:text-gray-300">
                Choose the plan that's right for you and start achieving your goals today.
              </motion.p>
            </motion.div>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-6xl grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              {pricingPlans.map((plan, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className={`relative rounded-2xl border p-8 ${
                    plan.popular
                      ? "border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/10"
                      : "border-gray-200 dark:border-gray-800"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center rounded-full bg-rose-500 px-3 py-1 text-xs font-medium text-white">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-text-primary dark:text-white mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-text-primary dark:text-white">
                        {plan.price}
                      </span>
                      {plan.period && (
                        <span className="text-text-secondary dark:text-gray-300">
                          {plan.period}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-text-secondary dark:text-gray-300">
                      {plan.description}
                    </p>
                  </div>
                  <ul className="mb-8 space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <Check className="size-4 text-rose-500" />
                        <span className="text-text-secondary dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate("/signup")}
                    variant={plan.popular ? "primary" : "secondary"}
                    ambient={true}
                    className="w-full px-4 py-2 text-sm"
                  >
                    {plan.cta}
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-20 sm:py-32 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center mb-16"
            >
              <motion.h2 variants={item} className="text-3xl font-bold tracking-tight text-text-primary dark:text-white sm:text-4xl mb-4">
                Frequently asked questions
              </motion.h2>
              <motion.p variants={item} className="text-lg text-text-secondary dark:text-gray-300">
                Everything you need to know about ThinkMate.
              </motion.p>
            </motion.div>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-3xl space-y-6"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="rounded-2xl bg-white dark:bg-gray-800 p-8"
                >
                  <h3 className="mb-4 text-lg font-semibold text-text-primary dark:text-white">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary dark:text-gray-300">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 sm:py-32 bg-gradient-to-br from-rose-500 to-blue-600">
          <div className="container mx-auto px-4">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center"
            >
              <motion.h2 variants={item} className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
                Ready to transform your goals?
              </motion.h2>
              <motion.p variants={item} className="mb-8 text-lg text-rose-100">
                Join thousands of users who are already achieving their goals with ThinkMate.
              </motion.p>
              <motion.div variants={item} className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <button
                  onClick={() => navigate("/signup")}
                  className="inline-flex items-center justify-center rounded-full bg-white px-8 py-3 text-base font-medium text-rose-600 transition-colors hover:bg-gray-100"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 size-4" />
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 px-8 py-3 text-base font-medium text-white transition-colors hover:bg-white/10"
                >
                  Sign In
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 font-bold text-xl mb-4">
                <div className="size-8 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
                  T
                </div>
                <span>ThinkMate</span>
              </div>
              <p className="text-gray-400">
                AI-powered goal achievement platform helping you build momentum and succeed.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/help" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>&copy; 2024 ThinkMate. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 